/*
 * VPDB - Virtual Pinball Database
 * Copyright (C) 2019 freezy <freezy@vpdb.io>
 *
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; either version 2
 * of the License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 * Modified for sigmashake-pinball by SigmaShake, 2026.
 */

import type { ItemFrame } from "../../../shared/contracts";
import { EventProxy } from "../../core/event-proxy";
import type { IHittable } from "../../core/ihittable";
import type { IMovable } from "../../core/imovable";
import { Item } from "../../core/item";
import type { Player } from "../../core/player";
import { degToRad } from "../../math/float";
import { Vertex2D } from "../../math/vertex2d";
import type { HitCircle } from "../../physics/hit-circle";
import type { HitObject } from "../../physics/hit-object";
import type { LineSeg } from "../../physics/line-seg";
import type { Table } from "../../table-model";
import type { GateData } from "./gate-data";
import type { GateHit } from "./gate-hit";
import { GateHitGenerator } from "./gate-hit-generator";
import type { GateMover } from "./gate-mover";
import { GateState } from "./gate-state";

/**
 * A gate. A ball swings it open in one direction (or in two directions).
 */
export class Gate extends Item<GateData> implements IMovable, IHittable {
  private readonly hitGenerator: GateHitGenerator;
  private readonly state: GateState;
  private hitGate?: GateHit;
  private hitLines?: LineSeg[];
  private hitCircles?: HitCircle[];

  public constructor(data: GateData) {
    super(data);
    this.state = new GateState(this.getName(), 0, data.showBracket, data.isVisible);
    this.hitGenerator = new GateHitGenerator(data);
  }

  public isCollidable(): boolean {
    return this.data.isCollidable;
  }

  public setupPlayer(player: Player, table: Table): void {
    const height = table.getSurfaceHeight(
      this.data.szSurface,
      this.data.center.x,
      this.data.center.y,
    );
    const radAngle = degToRad(this.data.rotation);
    const tangent = new Vertex2D(Math.cos(radAngle), Math.sin(radAngle));

    this.events = new EventProxy(this, "gate", player.getEventSink());
    this.hitGate = this.hitGenerator.generateGateHit(this.state, this.events, height);
    this.hitLines = this.hitGenerator.generateLineSegs(this.events, height, tangent);
    this.hitCircles = this.hitGenerator.generateBracketHits(
      this.state,
      this.events,
      height,
      tangent,
    );
  }

  public getHitShapes(): HitObject[] {
    return [this.hitGate!, ...this.hitLines!, ...this.hitCircles!];
  }

  public getMover(): GateMover {
    return this.hitGate!.getMoverObject();
  }

  public getState(): GateState {
    return this.state;
  }

  public toFrame(): ItemFrame {
    return this.state.toFrame();
  }
}
